import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { type CurrencyModelDto } from '@treasury/api/channel';
import { CurrenciesService } from '@treasury/domain/services/currencies.service';
import { WireAccount, WireCompany, WireIsoService, WiresService } from '@treasury/domain/wires';
import { TmBaseComponent } from '@treasury/presentation';
import { type SelectItemVm, type TmValueChanged } from '@treasury/presentation/components/forms';
import {
    type CurrencyRange,
    CurrencyRangeType,
} from '@treasury/presentation/components/tm-currency-range';
import { mapCurrencyRangeToDto } from '@treasury/presentation/components/tm-currency-range.mappings';
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import { mapDateRangeToDto } from '@treasury/presentation/components/tm-date-range.mappings';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import { add } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { WireStatusItem, wireStatusItems } from '../data/wire-payment-status-items';

import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-footer';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-date-range';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-currency-range';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-multi-select';
import '@treasury/presentation/components/tm-select';
import '@treasury/presentation/components/tm-text-field';

type WireType = 'Both' | 'Domestic' | 'International';
type WireFrequency =
    | 'Weekly'
    | 'EveryTwoWeeks'
    | 'TwiceAMonth'
    | 'Monthly'
    | 'Quarterly'
    | 'EverySixMonths'
    | 'Yearly';

const WireTypes: WireType[] = ['Both', 'Domestic', 'International'];

const wireTypesOptions = WireTypes.map(type => ({ label: type, value: type }));

const defaultEffectiveDateRange: DateRange = {
    rangeType: DateRangeType.DateRange,
    startDate: new Date(),
    endDate: add(new Date(), { weeks: 1 }),
};

const defaultNextPaymentDateRange: DateRange = {
    rangeType: DateRangeType.None,
};

const defaultCreatedDateRange: DateRange = {
    rangeType: DateRangeType.None,
};

const defaultAmountRange: CurrencyRange = {
    rangeType: CurrencyRangeType.AllAmounts,
};

const defaultState = {
    wireCompany: true,
    status: true,
    debitAccount: true,
    amountRange: true,
    effectiveDate: true,
    createdDate: true,
    frequency: true,
};

const frequencies: WireFrequency[] = [
    'Weekly',
    'EveryTwoWeeks',
    'TwiceAMonth',
    'Monthly',
    'Quarterly',
    'EverySixMonths',
    'Yearly',
];

export const tagName = 'wire-payments-filter-sheet';
@customElement(tagName)
export class WirePaymentsFilterSheet extends TmBaseComponent {
    @property()
    public debitAccounts: WireAccount[] = [];

    @property()
    public loading = true;

    @property({ type: Boolean })
    public open = false;

    @property({ type: Boolean })
    public isRecurring = false;

    @property({ type: Boolean })
    public hasWirePendingProcessStatus = true;

    @property({ type: Array })
    public wireCompanies: WireCompany[] = [];

    @InjectProperty()
    declare private wiresService: WiresService;

    @InjectProperty()
    declare private currenciesService: CurrenciesService;

    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @state()
    private wireTypesSelected: SelectItemVm<WireType> = wireTypesOptions[0];

    @state()
    private wireCompaniesSelected: WireCompany[] = [];

    @state()
    private statusItems = [...wireStatusItems];

    @state()
    private statusItemsSelected: WireStatusItem[] = [];

    @state()
    private debitAccountsSelected: WireAccount[] = [];

    @state()
    private amountRange: CurrencyRange = defaultAmountRange;

    @state()
    private createdDateRange: DateRange = defaultCreatedDateRange;

    @state()
    private effectiveDateRange: DateRange = defaultEffectiveDateRange;

    @state()
    private nextPaymentDateRange: DateRange = defaultNextPaymentDateRange;

    @state()
    private frequencyItemsSelected: WireFrequency[] = [];

    @state()
    private beneficiaryName?: string;

    @state()
    private transactionID?: string;

    @state()
    private currencyItems: SelectItemVm<CurrencyModelDto | 'none'>[] = [];

    @state()
    private currencyItemSelected?: CurrencyModelDto;

    @state()
    private omad?: string;

    @state()
    private wireIsoLabels = WireIsoService.legacyLabels;

    @DeepReactive()
    private validState = defaultState;

    protected async firstUpdated() {
        this.wireIsoLabels = await this.wireIsoService.getLabels();
    }

    protected updated(changedProperties: Map<string, unknown>) {
        if (changedProperties.has('loading') && !this.loading) {
            this.setFilterOptions();
            this.onResetForm();
        }
        if (
            changedProperties.has('hasWirePendingProcessStatus') &&
            !this.hasWirePendingProcessStatus
        ) {
            this.statusItems = this.statusItems.filter(item => item.name !== 'Pending Process');
        }
    }

    protected render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
            }}
        >
            <p slot="header-center">
                Filter ${this.isRecurring ? 'Recurring' : nothing} Wire Payments
            </p>
            <p slot="header-right"></p>
            <tm-section class="p-3 mb-10">${this.renderForm()}</tm-section>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: 'Apply Filters',
                        onClick: () => {
                            this.onApplyFilters();
                        },
                        disabled: Object.values(this.validState).some(isValid => !isValid),
                    },
                    {
                        importance: 'secondary',
                        text: 'Reset',
                        onClick: () => {
                            this.onResetForm();
                        },
                    },
                ] as ButtonConfig[]}
            >
            </tm-footer>
        </tm-bottom-sheet> `;
    }

    private async setFilterOptions() {
        this.currencyItems = (await this.currenciesService.getAll()).map(mapWireCurrencyToVm);
        this.currencyItems.unshift({ label: '', value: 'none' });
        this.statusItemsSelected = [...this.statusItems];
        this.onApplyFilters();
    }

    private onApplyFilters() {
        this.dispatchEvent(
            new CustomEvent('applyFilters', {
                detail: {
                    oneTimeDto: this.toDto(false),
                    recurringDto: this.toDto(true),
                },
            })
        );
        this.dispatchEvent(new CustomEvent('close'));
    }

    private onResetForm() {
        this.wireCompaniesSelected = [...this.wireCompanies];
        [this.wireTypesSelected] = wireTypesOptions;
        this.debitAccountsSelected = [...this.debitAccounts];
        this.statusItemsSelected = [...this.statusItems];
        this.frequencyItemsSelected = [...frequencies];
        this.amountRange = { ...defaultAmountRange };
        this.effectiveDateRange = { ...defaultEffectiveDateRange };
        this.createdDateRange = { ...defaultCreatedDateRange };
        this.nextPaymentDateRange = { ...defaultNextPaymentDateRange };
        this.beneficiaryName = undefined;
        this.transactionID = undefined;
        this.currencyItemSelected = undefined;
        this.omad = undefined;
        this.validState = { ...defaultState };
    }

    private toDto(isRecurring: boolean) {
        const {
            operator: wireAmountType,
            fromAmount: wireAmountMin,
            toAmount: wireAmountMax,
            specificAmount: wireAmount,
        } = mapCurrencyRangeToDto(this.amountRange);

        const {
            operator: valueDateType,
            dateFrom: valueDateStart,
            dateTo: valueDateEnd,
            specificDate: valueDate,
        } = mapDateRangeToDto(this.effectiveDateRange);

        const {
            operator: nextPaymentDateType,
            dateFrom: nextPaymentDateStart,
            dateTo: nextPaymentDateEnd,
            specificDate: nextPaymentDate,
        } = mapDateRangeToDto(this.nextPaymentDateRange);

        const {
            operator: createdDateType,
            dateFrom: createdDateStart,
            dateTo: createdDateEnd,
            specificDate: createdDate,
        } = mapDateRangeToDto(this.createdDateRange);

        const request = {
            ...(this.wireCompaniesSelected.length && {
                wireCompanies: this.wireCompaniesSelected.map(item => item.toDto()),
            }),
            statuses: [...this.statusItemsSelected],
            ...(this.debitAccountsSelected.length && {
                debitAccounts: [...this.debitAccountsSelected],
            }),
            beneficiaryName: this.beneficiaryName,
            transactionID: this.transactionID,
            wireAmountType,
            wireAmount,
            wireAmountMin: wireAmountMax ? wireAmountMin : undefined,
            wireAmountMax,
            createdDateType,
            createdDate,
            createdDateStart,
            createdDateEnd,
        };
        const oneTimeRequest = {
            wireType: this.wireTypesSelected.value,
            valueDateType,
            valueDateStart,
            valueDateEnd,
            valueDate,
            currency: this.currencyItemSelected,
            omad: this.omad,
            isWireTotalsRequired: false,
            isDownloadReport: false,
        };
        const recurringRequest = {
            frequency: this.frequencyItemsSelected.map(mapWireFrequencyToVm).map(item => ({
                key: item.value,
                value: item.label,
            })),
            nextPaymentDateType,
            nextPaymentDateStart,
            nextPaymentDateEnd,
            nextPaymentDate,
        };

        return {
            ...request,
            ...(isRecurring ? recurringRequest : oneTimeRequest),
        };
    }

    private renderForm() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        return html`<form>
            <tm-multi-select
                label="Wire Company"
                .allowSelectAll=${true}
                .allSelectedText=${'All Wire Companies'}
                .items=${this.wireCompanies}
                .initial=${this.wireCompaniesSelected}
                .mapItem=${mapWireCompanyToVm}
                @value-changed=${(e: TmValueChanged<WireCompany[]>) => {
                    this.wireCompaniesSelected = e.detail.value || [];
                    this.validState.wireCompany = this.wireCompaniesSelected.length > 0;
                }}
            ></tm-multi-select>

            ${!this.isRecurring
                ? html`<tm-segmented-control
                      class="pt-2 pb-4"
                      label="Wire Type"
                      .options=${wireTypesOptions}
                      .initial=${this.wireTypesSelected}
                      @value-changed=${({ detail }: TmValueChanged<SelectItemVm<WireType>>) => {
                          if (!detail.value) {
                              return;
                          }

                          this.wireTypesSelected = detail.value;
                      }}
                  ></tm-segmented-control>`
                : nothing}

            <tm-multi-select
                label="Status"
                .allowSelectAll=${true}
                .allSelectedText=${'All Wire Statuses'}
                .items=${this.statusItems}
                .initial=${[...this.statusItemsSelected]}
                .mapItem=${mapWireStatusItemToVm}
                @value-changed=${(e: TmValueChanged<WireStatusItem[]>) => {
                    this.statusItemsSelected = e.detail.value || [];
                    this.validState.status = this.statusItemsSelected.length > 0;
                }}
            ></tm-multi-select>
            <tm-multi-select
                label="Debit Account"
                .allowSelectAll=${true}
                .allSelectedText=${'All Debit Accounts'}
                .items=${this.debitAccounts}
                .initial=${[...this.debitAccountsSelected]}
                .mapItem=${mapDebitAccountToVm}
                @value-changed=${(e: TmValueChanged<WireAccount[]>) => {
                    this.debitAccountsSelected = e.detail.value || [];
                    this.validState.debitAccount = this.debitAccountsSelected.length > 0;
                }}
            ></tm-multi-select>
            <tm-text-field
                label="${this.wireIsoLabels.creditor} Name"
                .initial=${this.beneficiaryName}
                @value-changed=${(e: CustomEvent) => {
                    this.beneficiaryName = e.detail.value;
                }}
            ></tm-text-field
            ><tm-text-field
                label="Transaction ID"
                .initial=${this.transactionID}
                @value-changed=${(e: CustomEvent) => {
                    this.transactionID = e.detail.value;
                }}
            ></tm-text-field>
            <tm-text-field
                label="OMAD"
                .initial=${this.omad}
                @value-changed=${(e: CustomEvent) => {
                    this.omad = e.detail.value;
                }}
            ></tm-text-field>

            ${!this.isRecurring
                ? html`<tm-select
                      label="Currency"
                      .items=${this.currencyItems}
                      .initial=${this.currencyItemSelected}
                      @value-changed=${(e: TmValueChanged<CurrencyModelDto | 'none'>) => {
                          const currencyItem =
                              e.detail.value === 'none' ? undefined : e.detail.value;
                          this.currencyItemSelected = currencyItem;
                      }}
                  >
                  </tm-select>`
                : nothing}

            <tm-currency-range
                .initial=${this.amountRange}
                @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                    const { isValid, value } = e.detail;
                    this.validState.amountRange = isValid;
                    if (!value) {
                        return;
                    }
                    this.amountRange = value;
                }}
            ></tm-currency-range>

            ${this.isRecurring
                ? html`<tm-multi-select
                          label="Frequency"
                          .allowSelectAll=${true}
                          .allSelectedText=${'All Frequencies'}
                          .items=${[...frequencies]}
                          .initial=${[...this.frequencyItemsSelected]}
                          .mapItem=${mapWireFrequencyToVm}
                          @value-changed=${(e: TmValueChanged<WireFrequency[]>) => {
                              this.frequencyItemsSelected = e.detail.value || [];
                              this.validState.frequency = this.frequencyItemsSelected.length > 0;
                          }}
                      ></tm-multi-select>

                      <tm-date-range
                          label="Next Payment Date"
                          .initial=${this.nextPaymentDateRange}
                          @value-changed=${(e: TmValueChanged<DateRange>) => {
                              const { isValid, value } = e.detail;
                              this.validState.effectiveDate = isValid;
                              if (value) {
                                  this.nextPaymentDateRange = value;
                              }
                          }}
                      ></tm-date-range>`
                : nothing}
            ${!this.isRecurring
                ? html`<tm-date-range
                      label="Effective Date"
                      .initial=${this.effectiveDateRange}
                      @value-changed=${(e: TmValueChanged<DateRange>) => {
                          const { isValid, value } = e.detail;
                          this.validState.effectiveDate = isValid;
                          if (value) {
                              this.effectiveDateRange = value;
                          }
                      }}
                  ></tm-date-range>`
                : nothing}

            <tm-date-range
                label="Created Date"
                .initial=${this.createdDateRange}
                @value-changed=${(e: TmValueChanged<DateRange>) => {
                    const { isValid, value } = e.detail;
                    this.validState.createdDate = isValid;
                    if (value) {
                        this.createdDateRange = value;
                    }
                }}
                .maxDate=${new Date()}
            ></tm-date-range>
        </form>`;
    }
}

function mapWireCompanyToVm(company: WireCompany): SelectItemVm<WireCompany> {
    return {
        label: company.isDliWireCompany ? `${company.name} - International` : company.name,
        value: company,
    };
}

function mapWireStatusItemToVm(status: WireStatusItem): SelectItemVm<WireStatusItem> {
    return {
        label: status.name,
        value: status,
    };
}

function mapDebitAccountToVm(account: WireAccount): SelectItemVm<WireAccount> {
    return {
        label: account.accountDisplayLabel,
        value: account,
    };
}

function mapWireCurrencyToVm(currency: CurrencyModelDto): SelectItemVm<CurrencyModelDto> {
    return {
        label: `${currency.currencyCode}, ${currency.name}`,
        value: currency,
    };
}

function mapWireFrequencyToVm(frequency: WireFrequency): SelectItemVm<WireFrequency> {
    const frequencyMap: Record<WireFrequency, string> = {
        Weekly: 'Weekly',
        EveryTwoWeeks: 'Every Two Weeks',
        TwiceAMonth: 'Twice A Month',
        Monthly: 'Monthly',
        Quarterly: 'Quarterly',
        EverySixMonths: 'Every Six Months',
        Yearly: 'Yearly',
    };

    if (!(frequency in frequencyMap)) {
        throw new Error(`Unknown wire frequency: ${frequency}`);
    }

    return {
        label: frequencyMap[frequency],
        value: frequency,
    };
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WirePaymentsFilterSheet;
    }
}
